// client/src/pages/admin/ManageCategories.js
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import adminService from '../../services/adminService.js';
import Button from '../../components/ui/Button.js';
import Input from '../../components/ui/Input.js';
import Modal from '../../components/ui/Modal.js';

// ——— Tab Navigation ———————————————————————————————
function TabNav({ activeTab, setActiveTab }) {
  const tabs = [
    { key: 'categories', label: 'eCard Categories' },
    { key: 'industries', label: 'Industry Types' },
    { key: 'contacts', label: 'Contact Types' },
  ];

  return (
    <div className="flex gap-2 mb-6">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => setActiveTab(tab.key)}
          className={`px-4 py-2 rounded-lg text-base font-medium transition-colors ${
            activeTab === tab.key
              ? 'bg-gold/15 text-gold border border-gold/30'
              : 'text-royal-200 border border-royal-600 hover:text-white hover:border-royal-400'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ——— eCard Categories Tab —————————————————————————————
function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    categoryname: '',
    description: '',
    displayorder: 0,
    isactive: true,
    isbusiness: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getMoonbeamCategories();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Load categories error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ categoryname: '', description: '', displayorder: 0, isactive: true, isbusiness: false });
    setError('');
    setShowModal(true);
  };

  const openEdit = (cat) => {
    setEditing(cat.categoryid);
    setForm({
      categoryname: cat.categoryname || '',
      description: cat.description || '',
      displayorder: cat.displayorder || 0,
      isactive: cat.isactive !== false,
      isbusiness: cat.isbusiness || false,
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await adminService.updateMoonbeamCategory(editing, form);
      } else {
        await adminService.createMoonbeamCategory(form);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.data?.error || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete "${cat.categoryname}"?`)) return;
    try {
      await adminService.deleteMoonbeamCategory(cat.categoryid);
      load();
    } catch (err) {
      alert(err.data?.error || 'Failed to delete category');
    }
  };

  if (loading) {
    return <div className="text-royal-200 text-center py-8">Loading categories...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-royal-200 text-sm">
          Manage eCard categories. Business categories are only visible to Business Wizard users.
        </p>
        <Button onClick={openNew}>Add Category</Button>
      </div>

      <div className="space-y-2">
        {categories.map((cat) => (
          <div
            key={cat.categoryid}
            className={`bg-royal-800/40 border rounded-lg p-4 flex items-center justify-between ${
              cat.isbusiness ? 'border-purple-500/50' : 'border-royal-600'
            }`}
          >
            <div className="flex items-center gap-4">
              <span className="text-royal-200 text-sm w-8">#{cat.displayorder}</span>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{cat.categoryname}</span>
                  {cat.isbusiness && (
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                      Business
                    </span>
                  )}
                  {!cat.isactive && (
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-royal-200 text-sm">
                  {cat.templatecount || 0} template{cat.templatecount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => openEdit(cat)} className="text-gold text-sm hover:text-gold-light">
                Edit
              </button>
              <button onClick={() => handleDelete(cat)} className="text-red-400 text-sm hover:text-red-300">
                Delete
              </button>
            </div>
          </div>
        ))}
        {categories.length === 0 && (
          <p className="text-royal-200 text-center py-8">No categories yet.</p>
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Category' : 'New Category'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-900/30 border border-red-600 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
          <Input
            label="Category Name"
            value={form.categoryname}
            onChange={(e) => setForm({ ...form, categoryname: e.target.value })}
            required
          />
          <Input
            label="Description (optional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <Input
            label="Display Order"
            type="number"
            value={form.displayorder}
            onChange={(e) => setForm({ ...form, displayorder: parseInt(e.target.value) || 0 })}
          />
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-royal-200 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.isactive}
                onChange={(e) => setForm({ ...form, isactive: e.target.checked })}
                className="w-4 h-4 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold"
              />
              Active
            </label>
            <label className="flex items-center gap-2 text-royal-200 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.isbusiness}
                onChange={(e) => setForm({ ...form, isbusiness: e.target.checked })}
                className="w-4 h-4 rounded border-royal-600 bg-royal-800 text-purple-500 focus:ring-purple-500"
              />
              Business Only
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ——— Industry Types Tab ———————————————————————————————
function IndustryTypesTab() {
  const [industryTypes, setIndustryTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    industryname: '',
    displayorder: 0,
    isactive: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getIndustryTypes();
      setIndustryTypes(data.industryTypes || []);
    } catch (err) {
      console.error('Load industry types error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ industryname: '', displayorder: 0, isactive: true });
    setError('');
    setShowModal(true);
  };

  const openEdit = (it) => {
    setEditing(it.industrytypeid);
    setForm({
      industryname: it.industryname || '',
      displayorder: it.displayorder || 0,
      isactive: it.isactive !== false,
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await adminService.updateIndustryType(editing, form);
      } else {
        await adminService.createIndustryType(form);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.data?.error || 'Failed to save industry type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Delete "${it.industryname}"?`)) return;
    try {
      await adminService.deleteIndustryType(it.industrytypeid);
      load();
    } catch (err) {
      alert(err.data?.error || 'Failed to delete industry type');
    }
  };

  if (loading) {
    return <div className="text-royal-200 text-center py-8">Loading industry types...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-royal-200 text-sm">
          Industry types are used to target business templates to specific industries.
        </p>
        <Button onClick={openNew}>Add Industry</Button>
      </div>

      <div className="space-y-2">
        {industryTypes.map((it) => (
          <div
            key={it.industrytypeid}
            className="bg-royal-800/40 border border-royal-600 rounded-lg p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <span className="text-royal-200 text-sm w-8">#{it.displayorder}</span>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{it.industryname}</span>
                  {!it.isactive && (
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-royal-200 text-sm">
                  {it.templatecount || 0} template{it.templatecount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => openEdit(it)} className="text-gold text-sm hover:text-gold-light">
                Edit
              </button>
              <button onClick={() => handleDelete(it)} className="text-red-400 text-sm hover:text-red-300">
                Delete
              </button>
            </div>
          </div>
        ))}
        {industryTypes.length === 0 && (
          <p className="text-royal-200 text-center py-8">No industry types yet.</p>
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Industry Type' : 'New Industry Type'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-900/30 border border-red-600 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
          <Input
            label="Industry Name"
            value={form.industryname}
            onChange={(e) => setForm({ ...form, industryname: e.target.value })}
            required
          />
          <Input
            label="Display Order"
            type="number"
            value={form.displayorder}
            onChange={(e) => setForm({ ...form, displayorder: parseInt(e.target.value) || 0 })}
          />
          <label className="flex items-center gap-2 text-royal-200 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.isactive}
              onChange={(e) => setForm({ ...form, isactive: e.target.checked })}
              className="w-4 h-4 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold"
            />
            Active
          </label>
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ——— Contact Types Tab ————————————————————————————————
function ContactTypesTab() {
  const [contactTypes, setContactTypes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    typename: '',
    classid: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getContactTypes();
      setContactTypes(data.contactTypes || []);
      setClasses(data.classes || []);
    } catch (err) {
      console.error('Load contact types error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ typename: '', classid: classes[0]?.classid || '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (ct) => {
    setEditing(ct.contacttypeid);
    setForm({
      typename: ct.typename || '',
      classid: ct.classid || '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await adminService.updateContactType(editing, form);
      } else {
        await adminService.createContactType(form);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.data?.error || 'Failed to save contact type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ct) => {
    if (!window.confirm(`Delete "${ct.typename}"?`)) return;
    try {
      await adminService.deleteContactType(ct.contacttypeid);
      load();
    } catch (err) {
      alert(err.data?.error || 'Failed to delete contact type');
    }
  };

  // Group contact types by class
  const personalTypes = contactTypes.filter((ct) => ct.classid === 1);
  const businessTypes = contactTypes.filter((ct) => ct.classid === 2);

  if (loading) {
    return <div className="text-royal-200 text-center py-8">Loading contact types...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-royal-200 text-sm">
          Default contact types available to all users. Users can also create their own custom types.
        </p>
        <Button onClick={openNew}>Add Contact Type</Button>
      </div>

      {/* Personal Types */}
      <div className="mb-6">
        <h3 className="text-royal-100 font-medium mb-2 flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-blue-500"></span>
          Personal ({personalTypes.length})
        </h3>
        <div className="space-y-2">
          {personalTypes.map((ct) => (
            <div
              key={ct.contacttypeid}
              className="bg-royal-800/40 border border-royal-600 rounded-lg p-3 flex items-center justify-between"
            >
              <span className="text-white">{ct.typename}</span>
              <div className="flex gap-3">
                <button onClick={() => openEdit(ct)} className="text-gold text-sm hover:text-gold-light">
                  Edit
                </button>
                <button onClick={() => handleDelete(ct)} className="text-red-400 text-sm hover:text-red-300">
                  Delete
                </button>
              </div>
            </div>
          ))}
          {personalTypes.length === 0 && (
            <p className="text-royal-200 text-sm py-2">No personal contact types.</p>
          )}
        </div>
      </div>

      {/* Business Types */}
      <div>
        <h3 className="text-royal-100 font-medium mb-2 flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-purple-500"></span>
          Business ({businessTypes.length})
        </h3>
        <div className="space-y-2">
          {businessTypes.map((ct) => (
            <div
              key={ct.contacttypeid}
              className="bg-royal-800/40 border border-royal-600 rounded-lg p-3 flex items-center justify-between"
            >
              <span className="text-white">{ct.typename}</span>
              <div className="flex gap-3">
                <button onClick={() => openEdit(ct)} className="text-gold text-sm hover:text-gold-light">
                  Edit
                </button>
                <button onClick={() => handleDelete(ct)} className="text-red-400 text-sm hover:text-red-300">
                  Delete
                </button>
              </div>
            </div>
          ))}
          {businessTypes.length === 0 && (
            <p className="text-royal-200 text-sm py-2">No business contact types.</p>
          )}
        </div>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Contact Type' : 'New Contact Type'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-900/30 border border-red-600 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
          <Input
            label="Type Name"
            value={form.typename}
            onChange={(e) => setForm({ ...form, typename: e.target.value })}
            placeholder="e.g., College Friends"
            required
          />
          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Class</label>
            <select
              value={form.classid}
              onChange={(e) => setForm({ ...form, classid: parseInt(e.target.value) })}
              className="w-full px-3 py-2.5 bg-royal-900/60 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50"
              required
            >
              {classes.map((c) => (
                <option key={c.classid} value={c.classid}>
                  {c.classname}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ——— Main Component ———————————————————————————————————
export default function ManageCategories() {
  const [activeTab, setActiveTab] = useState('categories');

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <h1 className="font-display text-3xl text-gold mb-6">Manage Categories</h1>

      <TabNav activeTab={activeTab} setActiveTab={setActiveTab} />

      {activeTab === 'categories' && <CategoriesTab />}
      {activeTab === 'industries' && <IndustryTypesTab />}
      {activeTab === 'contacts' && <ContactTypesTab />}
    </div>
  );
}
